import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
    discardFailedAction,
    getFailedActions,
    retryFailedAction,
    syncPendingActions,
    type FailedActionSummary,
} from "@/lib/offline-sync";
import { failureNextStep } from "@/lib/offline-replay";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/app-dialogs";

const POLL_MS = 5_000;

/**
 * OFFLINE-QUEUE HONESTY — the "N saves didn't sync" chip.
 *
 * A queued write whose replay ultimately failed server-side (e.g. 400
 * REASON_REQUIRED on a disposition) used to vanish with a console.warn.
 * Now the drain parks it in the outbox (see lib/offline-sync.ts) and this
 * chip makes the failure impossible to miss: a persistent red pill in the
 * app shell that opens a review sheet — what failed, which job, the
 * server's actual error message, a retry button, and (for terminal 4xx
 * only) discard-with-confirm. Payloads live in IndexedDB, so the list
 * survives reloads and app restarts.
 */
export function FailedSavesChip() {
    const [failures, setFailures] = useState<FailedActionSummary[]>([]);
    const [open, setOpen] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [retryingAll, setRetryingAll] = useState(false);

    // Martin High School (10/27): a queued test replayed into a rejection and
    // the tech never heard why — the drain parks quietly (often in the service
    // worker, which can't toast). Say it once, in the server's own words, the
    // moment a NEW entry is parked. Entries parked before this screen loaded
    // are not re-announced; the red chip already carries them.
    const mountedAt = useRef(Date.now());
    const announced = useRef(new Set<string>());
    const markAnnounced = useCallback((list: FailedActionSummary[]) => {
        for (const f of list) announced.current.add(`${f.id}:${f.failure.at}`);
    }, []);
    const refresh = useCallback(() => {
        void getFailedActions().then((list) => {
            setFailures(list);
            for (const f of list) {
                const key = `${f.id}:${f.failure.at}`;
                if (announced.current.has(key)) continue;
                announced.current.add(key);
                if (f.failure.at < mountedAt.current) continue;
                toast.error(`${describeFailedAction(f)} didn't send`, {
                    description: f.failure.message,
                    duration: 15_000,
                    closeButton: true,
                });
            }
        });
    }, []);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, POLL_MS);
        const onFocus = () => refresh();
        // The SW posts this after a Background Sync drain — a drain is
        // exactly when new failures get parked.
        const onSwMessage = (event: MessageEvent) => {
            const data = event.data as { type?: string } | null;
            if (data?.type === "OFFLINE_QUEUE_DRAINED") refresh();
        };
        window.addEventListener("focus", onFocus);
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", onSwMessage);
        }
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.removeEventListener("message", onSwMessage);
            }
        };
    }, [refresh]);

    const handleRetry = useCallback(
        async (item: FailedActionSummary) => {
            setBusyId(item.id);
            try {
                const ok = await retryFailedAction(item.id);
                if (!ok) {
                    refresh();
                    return;
                }
                await syncPendingActions();
                // Judge success by whether THIS entry cleared, not the drain's
                // whole-queue `synced` count — with other pending rows in the
                // outbox that total would mislead. A cleared entry drops out of
                // the failed list; a re-rejected one re-parks under the same id.
                const remaining = await getFailedActions();
                // The tech is looking at the list and gets the toast below —
                // don't also announce the re-parked row as new.
                markAnnounced(remaining);
                setFailures(remaining);
                if (remaining.some((f) => f.id === item.id)) {
                    toast.error("Still didn't send. Read the note on it for what to do.");
                } else {
                    toast.success("Save synced.");
                }
            } finally {
                setBusyId(null);
            }
        },
        [refresh, markAnnounced],
    );

    // Clears the whole backlog in one tap — un-park every entry, then a single
    // drain replays them all. The common case here is a batch of saves that
    // already landed server-side (each replay hits an idempotent 409 the drain
    // now treats as success), so a stack of "didn't sync" rows clears at once
    // instead of one Retry tap per row.
    const handleRetryAll = useCallback(async () => {
        setRetryingAll(true);
        try {
            const current = await getFailedActions();
            for (const f of current) {
                await retryFailedAction(f.id);
            }
            await syncPendingActions();
            const remaining = await getFailedActions();
            markAnnounced(remaining);
            setFailures(remaining);
            const cleared = current.length - remaining.length;
            if (cleared <= 0) {
                toast.error("Still didn't send. Read the note on each one for what to do.");
            } else if (remaining.length === 0) {
                toast.success(`${cleared} save${cleared === 1 ? "" : "s"} synced.`);
            } else {
                toast.success(`${cleared} synced · ${remaining.length} still need attention.`);
            }
        } finally {
            setRetryingAll(false);
        }
    }, [markAnnounced]);

    const handleDiscard = useCallback(
        async (item: FailedActionSummary) => {
            const confirmed = await confirmDialog({
                title: "Discard this save?",
                message:
                    `${describeFailedAction(item)}\n\n` +
                    `Why it didn't send: ${item.failure.message}\n\n` +
                    "Discarding permanently deletes the entered data from this device. " +
                    "If it should still be recorded, fix the problem in the workflow and save again instead.",
                confirmLabel: "Discard save",
                destructive: true,
            });
            if (!confirmed) return;
            setBusyId(item.id);
            try {
                await discardFailedAction(item.id);
                refresh();
            } finally {
                setBusyId(null);
            }
        },
        [refresh],
    );

    if (failures.length === 0) return null;

    return (
        <>
            <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 pointer-events-none">
                <button
                    type="button"
                    data-testid="failed-saves-chip"
                    onClick={() => setOpen(true)}
                    className="pointer-events-auto flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-red-700 transition-colors"
                >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {failures.length} {failures.length === 1 ? "save" : "saves"} didn&apos;t sync — tap to review
                </button>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent className="w-full sm:w-[440px] overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                            <AlertTriangle className="h-5 w-5" />
                            Saves that didn&apos;t sync
                        </SheetTitle>
                    </SheetHeader>
                    <div className="px-4 pb-4">
                        <p className="mt-3 text-xs text-foreground/70">
                            These entries are stored safely on this device and will NOT upload until you act. Retry
                            after fixing the problem; discard only if the data shouldn&apos;t be recorded at all.
                        </p>
                        {failures.length > 1 && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="mt-3 w-full"
                                data-testid="failed-saves-retry-all"
                                disabled={retryingAll || busyId !== null}
                                onClick={() => void handleRetryAll()}
                            >
                                {retryingAll ? (
                                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                ) : (
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                )}
                                Retry all {failures.length}
                            </Button>
                        )}
                        <ul className="mt-3 space-y-3">
                            {failures.map((item) => (
                                <li
                                    key={item.id}
                                    data-testid="failed-save-row"
                                    className="rounded-lg border border-red-200 dark:border-red-500/40 bg-red-50/60 dark:bg-red-950/20 p-3"
                                >
                                    <p className="text-sm font-semibold text-foreground">
                                        {describeFailedAction(item)}
                                    </p>
                                    <p className="text-[11px] text-foreground/70 mt-0.5">
                                        Saved {new Date(item.createdAt).toLocaleString()}
                                        {item.attempts > 0
                                            ? ` · ${item.attempts} sync attempt${item.attempts === 1 ? "" : "s"}`
                                            : ""}
                                    </p>
                                    <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">
                                        {item.failure.message}
                                    </p>
                                    <p data-testid="failed-save-next-step" className="mt-1 text-xs text-foreground/80">
                                        {failureNextStep(item.failure)}
                                    </p>
                                    <div className="mt-2 flex items-center gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={busyId === item.id || retryingAll}
                                            onClick={() => void handleRetry(item)}
                                        >
                                            {busyId === item.id ? (
                                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Retry
                                        </Button>
                                        {item.failure.terminal && (
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                disabled={busyId === item.id || retryingAll}
                                                onClick={() => void handleDiscard(item)}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                                Discard…
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}

/** Plain label for a queued write — what it was + which job/device. Never a
 *  web address or a method name: staff read this. */
export function describeFailedAction(item: Pick<FailedActionSummary, "url" | "method" | "body">): string {
    const body = (item.body ?? {}) as Record<string, unknown>;
    const shortId = (v: unknown) => (typeof v === "string" && v.length >= 8 ? v.slice(0, 8) : null);
    const url = item.url.split("?")[0];
    const jobMatch = url.match(/^\/api\/(?:jobs|backflow\/job|fire-safety\/job|field-adds\/jobs)\/([^/]+)/);
    const job = shortId(jobMatch?.[1]) ?? shortId(body.jobId);
    const onJob = job ? ` — job ${job}` : "";

    if (/^\/api\/jobs\/[^/]+\/assets\/[^/]+\/disposition$/.test(url)) return `Device skip/release${onJob}`;
    if (url === "/api/backflow/test") {
        const asset = shortId(body.assetId);
        return `Backflow test save${onJob}${asset ? `, device ${asset}` : ""}`;
    }
    if (/^\/api\/backflow\/job\/[^/]+\/submit$/.test(url)) return `Test report submit${onJob}`;
    if (url.includes("/attachments")) return `Photo upload${onJob}`;
    if (/\/devices\/[^/]+\/complete-test$/.test(url)) return `Test points${onJob}`;
    if (/\/devices\/[^/]+\/record-repair$/.test(url)) return `Repair points${onJob}`;
    if (url === "/api/backflow/repair") return `Repair save${onJob}`;
    if (url.startsWith("/api/backflow/replacement/")) return `Replacement save${onJob}`;
    if (/^\/api\/sites\/[^/]+\/assets$/.test(url) || /^\/api\/assets\//.test(url)) return `Device save${onJob}`;
    if (/^\/api\/jobs\/[^/]+\/assets$/.test(url)) return `Device added to job${onJob}`;
    if (url.startsWith("/api/field-adds/")) return `Note for the office${onJob}`;
    if (url.includes("signature")) return `Signature${onJob}`;
    if (url.includes("work-order")) return `Work order save${onJob}`;
    if (url.startsWith("/api/fire-safety/")) return `Fire inspection save${onJob}`;
    if (/\/(status|on-my-way|return-needed|leave-answer)$/.test(url)) return `Job status update${onJob}`;
    if (url.endsWith("/notes")) return `Job note${onJob}`;
    if (url.includes("/contact-card/")) return `Customer contact update${onJob}`;
    if (url === "/api/field/flag" || url.endsWith("/flag-workflow-issue")) return `Problem report${onJob}`;
    if (url.startsWith("/api/parts-to-stage/")) return `Parts check-off${onJob}`;
    return `A saved change${onJob}`;
}
